import {
  UseSelectGetToggleButtonPropsOptions,
  GetPropsCommonOptions,
  UseComboboxGetInputPropsOptions,
  UseComboboxGetComboboxPropsOptions,
  UseMultipleSelectionGetDropdownProps,
} from 'downshift';
import * as React from 'react';
import { useRef, useImperativeHandle } from 'react';
import { CaretDown, Close, Search } from '@tcl-boron-icons/icons';
import { DropdownOption } from '@tcl-argon-models/models';

import { itemsToString } from './coreUtils';

export interface InputParentRef {
  focusInput: () => void;
  inputWrapper: HTMLDivElement | null;
  input: HTMLInputElement | null;
}
export const useInputRef: () => React.MutableRefObject<InputParentRef> = () =>
  useRef({
    input: null,
    inputWrapper: null,
    focusInput: () => {},
  });

interface InputProps {
  classes: Record<string, string>;
  disabled: boolean;
  tabIndex: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getToggleButtonProps: (options?: UseSelectGetToggleButtonPropsOptions, otherOptions?: GetPropsCommonOptions) => any;
  selectedItems: Array<DropdownOption>;
  placeholder: string;
  shouldShowClearbutton: boolean;
  isComboBox: boolean;
  isTypeahead: boolean;
  isOpen: boolean;
  clearInput: (evt: React.MouseEvent<HTMLButtonElement>) => void;
  // for comboBox
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getInputProps?: (options?: UseComboboxGetInputPropsOptions, otherOptions?: GetPropsCommonOptions) => any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getComboboxProps?: (options?: UseComboboxGetComboboxPropsOptions, otherOptions?: GetPropsCommonOptions) => any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getDropdownProps?: (options?: UseMultipleSelectionGetDropdownProps, extraOptions?: GetPropsCommonOptions) => any;
  searchTerm?: string;
  isSingleSelect?: boolean;
  selectedItemsTextOverride?: string;
  handleKeyPress?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
}
export const Input: React.ForwardRefExoticComponent<InputProps & React.RefAttributes<InputParentRef>> =
  React.forwardRef(
    (
      {
        classes,
        disabled,
        tabIndex,
        getToggleButtonProps,
        selectedItems,
        placeholder,
        children,
        shouldShowClearbutton,
        getInputProps = () => ({}),
        getComboboxProps = () => ({}),
        getDropdownProps = () => ({}),
        isComboBox,
        isTypeahead,
        clearInput,
        isOpen,
        isSingleSelect,
        selectedItemsTextOverride,
        handleKeyPress,
      },
      inputParentRef,
    ) => {
      const inputWrapperRef: React.MutableRefObject<HTMLDivElement | null> = useRef(null);
      const inputRef: React.MutableRefObject<HTMLInputElement | null> = useRef(null);

      useImperativeHandle(inputParentRef, () => ({
        focusInput: () => {
          inputRef.current?.focus();
        },
        get inputWrapper() {
          return inputWrapperRef.current;
        },
        get input() {
          return inputRef.current;
        },
      }));
      const dropdownProps = getDropdownProps({ preventKeyAction: isOpen }, { suppressRefError: true });
      const inputProps = getInputProps(dropdownProps, { suppressRefError: true });
      const comboBoxProps = getComboboxProps();
      const toggleButtonProps = getToggleButtonProps({
        ...(!isComboBox ? dropdownProps : {}),
        className: `${classes.valueContainer} ${
          isComboBox && !disabled ? classes.valueContainerComboBox : classes.valueContainerDropdown
        } ${disabled ? classes.disabled : ''}`,
        tabIndex,
        disabled,
      });
      const showSelectedItems = selectedItems.length > 0 && (!isComboBox || (isComboBox && !isOpen));
      const showPlaceholderText = selectedItems.length === 0 && !isComboBox;
      const showSearchBox = isComboBox;
      const selectedItemsText = isSingleSelect
        ? itemsToString(selectedItems)
        : selectedItems.length === 1
        ? `(${selectedItems.length}) ${selectedItemsTextOverride ? selectedItemsTextOverride : `Option selected`}`
        : `(${selectedItems.length}) ${selectedItemsTextOverride ? selectedItemsTextOverride : `Options selected`}`;

      return (
        <div className={classes.inputWrapper} ref={inputWrapperRef} onKeyDown={(e) => handleKeyPress(e)}>
          <div {...toggleButtonProps}>
            <div {...{ ...(isComboBox ? comboBoxProps : {}) }} className={classes.valueWrapper}>
              {showSelectedItems && <span className={classes.selectedValuesText}>{selectedItemsText}</span>}
              {showPlaceholderText && <p className={`${classes.value} ${classes.placeholder}`}>{placeholder}</p>}
              {isComboBox && (
                <input
                  style={{ visibility: !showSearchBox ? 'hidden' : '' }}
                  {...{
                    ...inputProps,
                    type: 'text',
                    className: `${classes.searchBox} ${!isOpen ? classes.searchBoxPositionClosed : ''}`,
                    tabIndex: disabled ? -1 : 0,
                    readOnly: disabled,
                  }}
                  ref={inputRef}
                  placeholder={placeholder}
                ></input>
              )}
            </div>
            {shouldShowClearbutton && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div className={classes.buttonWrapper}>
                  <button
                    aria-label="clear selection"
                    className={classes.clearButton}
                    disabled={disabled}
                    onClick={clearInput}
                  >
                    <Close height="10px" width="10px" />
                  </button>
                </div>
                <div className={classes.buttonDivider} />
              </div>
            )}
            {(isComboBox || isTypeahead) && <Search height="14px" width="14px" />}
            {!isComboBox && !isTypeahead && <CaretDown height="10px" width="10px" />}
          </div>
          {children}
        </div>
      );
    },
  );
Input.displayName = 'Input';
